import { startOfDay } from 'date-fns'
import { defaultsDeep } from 'lodash-es'
import { createWithEqualityFn as create } from 'zustand/traditional'
import { createJSONStorage, persist } from 'zustand/middleware'
import { useRouter } from 'next/router'

import {
  ASSET_FILTER_TOGGLES,
  type AssetFilters,
  GEOFENCE_FILTER_TOGGLES,
  type GeoFenceFilters,
} from '@/feature/observatory/filters'
import { Asset } from '@/feature/observatory'

import { PersistentObservatoryPageStore } from './useObservatoryPageStore.types'

// Create a singleton to store the router instance
// This allows us to use the router outside of React components
let routerInstance: ReturnType<typeof useRouter> | null = null

export const setRouterInstance = (router: ReturnType<typeof useRouter>) => {
  routerInstance = router
}

export const usePersistentObservatoryPageStore = create(
  persist<PersistentObservatoryPageStore>(
    (setState, getState) => ({
      asides: {
        hideGeoFences: false,
        setHideGeoFences: (hideGeoFences: boolean) =>
          setState({ asides: { ...getState().asides, hideGeoFences } }),
        hideAssets: false,
        setHideAssets: (hideAssets: boolean) =>
          setState({ asides: { ...getState().asides, hideAssets } }),
      },
      toggles: {
        assets: true,
        setAssets: (assets: boolean) => setState({ toggles: { ...getState().toggles, assets } }),
        geoFences: true,
        setGeoFences: (geoFences: boolean) =>
          setState({ toggles: { ...getState().toggles, geoFences } }),
        traffic: false,
        setTraffic: (traffic: boolean) => setState({ toggles: { ...getState().toggles, traffic } }),
        satellite: false,
        setSatellite: (satellite: boolean) =>
          setState({ toggles: { ...getState().toggles, satellite } }),
        tasks: true,
        setTasks: (tasks: boolean) => {
          if (tasks) {
            setState({
              toggles: { ...getState().toggles, tasks, liveTasks: true, scheduledTasks: true },
            })
          } else {
            setState({
              toggles: { ...getState().toggles, tasks, liveTasks: false, scheduledTasks: false },
            })
          }
        },
        liveTasks: true,
        setLiveTasks: (liveTasks: boolean) =>
          setState({ toggles: { ...getState().toggles, liveTasks } }),
        scheduledTasks: true,
        setScheduledTasks: (scheduledTasks: boolean) =>
          setState({ toggles: { ...getState().toggles, scheduledTasks } }),
      },
      vehicleLabels: {
        showRegistration: false,
        showShareReference: false,
        showTags: false,
        setShowRegistration: (showRegistration: boolean) =>
          setState({
            vehicleLabels: { ...getState().vehicleLabels, showRegistration },
          }),
        setShowShareReference: (showShareReference: boolean) =>
          setState({
            vehicleLabels: { ...getState().vehicleLabels, showShareReference },
          }),
        setShowTags: (showTags: boolean) =>
          setState({
            vehicleLabels: { ...getState().vehicleLabels, showTags },
          }),
        clearVehicleLabels: () =>
          setState({
            vehicleLabels: {
              ...getState().vehicleLabels,
              showRegistration: false,
              showShareReference: false,
              showTags: false,
            },
          }),
      },
      selectedItem: undefined,
      setSelectedItem: (selectedItem: PersistentObservatoryPageStore['selectedItem']) =>
        setState({ selectedItem }),
      setSearchLocation: (searchLocation: PersistentObservatoryPageStore['searchLocation']) => {
        setState({ searchLocation })
      },
      assetsFilters: {
        toggles: { ...ASSET_FILTER_TOGGLES },
        sortOn: 'Alphabetically',
      },
      setAssetsFilters: (assetsFilters: AssetFilters) => setState({ assetsFilters }),
      assetsSearch: '',
      setAssetsSearch: (assetsSearch: string) => setState({ assetsSearch }),
      setGeoFenceFilters: (geoFenceFilters: GeoFenceFilters) => setState({ geoFenceFilters }),
      geoFenceFilters: {
        toggles: { ...GEOFENCE_FILTER_TOGGLES },
        sortOn: 'Date Created',
      },
      geoFenceSearch: '',
      setGeoFenceSearch: (geoFenceSearch: string) => setState({ geoFenceSearch }),
      replay: {
        asset: undefined,
        setAsset: (asset?: Asset['id']) => setState({ replay: { ...getState().replay, asset } }),

        selectionStart: startOfDay(new Date()),
        setSelectionStart: (selectionStart: Date) =>
          setState({ replay: { ...getState().replay, selectionStart } }),
        selectionEnd: new Date(),
        setSelectionEnd: (selectionEnd: Date) =>
          setState({ replay: { ...getState().replay, selectionEnd } }),

        granularity: 5,
        setGranularity: (granularity: number) =>
          setState({ replay: { ...getState().replay, granularity } }),
      },
      eta: {
        etaLocationFrom: undefined,
        etaLocationTo: undefined,
        showEta: false,
        setEtaLocationFrom: (location) =>
          setState({ eta: { ...getState().eta, etaLocationFrom: location } }),
        setEtaLocationTo: (location) =>
          setState({ eta: { ...getState().eta, etaLocationTo: location } }),
        setShowEta: (showEta) => setState({ eta: { ...getState().eta, showEta: showEta } }),
      },

      geoFenceModalId: undefined,
      setGeoFenceModalId: (geoFenceModalId: number | undefined) => setState({ geoFenceModalId }),

      geoFenceEditId: undefined,
      setGeoFenceEditId: (geoFenceEditId: number | undefined) => setState({ geoFenceEditId }),

      taskId: undefined,
      setTaskId: (taskId: string | undefined) => setState({ taskId }),

      focusedTaskId: undefined,
      setFocusedTaskId: (focusedTaskId: string | undefined) => setState({ focusedTaskId }),
    }),
    {
      name: 'page-state',
      storage: createJSONStorage(() => {
        // Track the last value to avoid unnecessary updates
        const lastValues: Record<string, string> = {}

        return {
          getItem: (name) => {
            const searchParams = new URLSearchParams(window.location.search)
            return searchParams.get(name)
          },
          setItem: (name, value) => {
            // Skip update if value hasn't changed
            if (lastValues[name] === value) {
              return
            }

            lastValues[name] = value

            if (routerInstance) {
              // Use the router to update the URL with the new query parameter
              const query = { ...routerInstance.query }
              query[name] = value

              // Use shallow routing to update the URL without reloading the page
              routerInstance.push(
                {
                  pathname: window.location.pathname,
                  query,
                },
                undefined,
                { shallow: true },
              )
            } else {
              // Fallback to window.history if router is not available
              const newUrl = new URL(window.location.href)
              newUrl.searchParams.set(name, value)
              window.history.pushState(window.history.state, '', newUrl.toString())
            }
          },
          removeItem: (name) => {
            // Skip if already removed
            if (lastValues[name] === undefined) {
              return
            }

            delete lastValues[name]

            if (routerInstance) {
              // Use the router to update the URL without the query parameter
              const query = { ...routerInstance.query }
              delete query[name]

              // Use shallow routing to update the URL without reloading the page
              routerInstance.push(
                {
                  pathname: window.location.pathname,
                  query,
                },
                undefined,
                { shallow: true },
              )
            } else {
              // Fallback to window.history if router is not available
              const newUrl = new URL(window.location.href)
              newUrl.searchParams.delete(name)
              window.history.pushState(window.history.state, '', newUrl.toString())
            }
          },
        }
      }),
      merge: defaultsDeep,
      onRehydrateStorage: (state) => (persistedState) => {
        // We're doing this manually here,
        // but if there are more properties that need manual deserialization, it might be worth using zed
        if (persistedState?.replay?.selectionStart) {
          state.replay.setSelectionStart(new Date(persistedState.replay.selectionStart))
        }
        if (persistedState?.replay?.selectionEnd) {
          state.replay.setSelectionEnd(new Date(persistedState.replay.selectionEnd))
        }
      },
    },
  ),
)
